import { reactive, toRaw } from 'vue';
import type { Item } from '../multiple-select-scripts';
import { multipleSelectGetSelects, multipleSelectSetSelects } from '../multiple-select-scripts';
import { populateNewFileDocumentClients } from './add-file-documents';
import type { MultipleSelectClient } from './add-file.types';
import { getClientsFromEnabledEmailInputs } from './form-clients';
import { ADD_CLIENT_FORM_SELECTOR, getWireDocRecipientsSelector } from './selectors';

/**
 * File type for client data storage
 * - "new"  - for "add file" form
 * - "file" - for "edit file/documents/etc" forms
 * - "old"  - for previously entered Clients (was formerly used by Closing Market integration)
 */
export enum ClientContext {
    NEW = 'new',
    FILE = 'file',
    OLD = 'old',
}

type AvailableClients = Record<ClientContext, MultipleSelectClient[]>;

export const availableClients = reactive<AvailableClients>({
    new: [],
    file: [],
    old: [],
});

export const setAvailableClients = (clientContext: ClientContext, clientsList: MultipleSelectClient[]) => {
    availableClients[clientContext] = clientsList;
};

export const getAvailableClients = (clientContext: ClientContext | ClientContext[]): MultipleSelectClient[] => {
    const clientContexts = Array.isArray(clientContext) ? clientContext : [clientContext];
    const result: MultipleSelectClient[] = clientContexts.flatMap(clientContext => availableClients[clientContext] ?? []);
    return structuredClone(result.map(toRaw));
};

export const resetAvailableClients = () => {
    availableClients.new = [];
    availableClients.file = [];
    availableClients.old = [];
};

export const populateRecipientsList = (selector: string, clientContext: ClientContext) => {
    const items: Item[] = getAvailableClients(clientContext).map(client => ({
        value: client.value,
        text: client.text,
    }));
    const selected = multipleSelectGetSelects(selector).map(Number);
    multipleSelectSetSelects(selector, items, selected);
};

export const updateRecipientsLists = (formId = ADD_CLIENT_FORM_SELECTOR) => {
    const existingClients = getAvailableClients(ClientContext.NEW);
    // Set the available Clients as the ones with email inputs that are not 'disabled'
    const clients = getClientsFromEnabledEmailInputs(formId, existingClients);
    setAvailableClients(ClientContext.NEW, clients);

    // Update the recipient dropdowns only if they exist in the current form
    const wireDocRecipientsSelector = getWireDocRecipientsSelector(formId);
    if ($(wireDocRecipientsSelector).length > 0) {
        populateRecipientsList(wireDocRecipientsSelector, ClientContext.NEW);
    }
    populateNewFileDocumentClients(formId);
};
